// Achievement definitions and localStorage-based storage for web users

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  earned: boolean;
  earnedAt?: string;
}

export interface StreakData {
  current: number;
  best: number;
  lastPracticeDate: string;
}

interface AchievementStorage {
  earnedAchievements: string[];
  streakData: StreakData;
  achievementTimestamps: Record<string, string>;
  totalPracticeCount: number;
  totalCorrectAnswers: number;
}

const STORAGE_KEY = 'examflow_achievements';

const DEFAULT_STORAGE: AchievementStorage = {
  earnedAchievements: [],
  streakData: {
    current: 0,
    best: 0,
    lastPracticeDate: '',
  },
  achievementTimestamps: {},
  totalPracticeCount: 0,
  totalCorrectAnswers: 0,
};

function getStorage(): AchievementStorage {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STORAGE };
    return JSON.parse(raw) as AchievementStorage;
  } catch {
    return { ...DEFAULT_STORAGE };
  }
}

function saveStorage(storage: AchievementStorage): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
  } catch (error) {
    console.error('[achievements] Failed to save storage:', error);
  }
}

export const ACHIEVEMENT_DEFS: Omit<Achievement, 'earned' | 'earnedAt'>[] = [
  {
    id: 'first-step',
    name: '完成首次练习',
    description: '完成1次练习',
    icon: '🎯',
  },
  {
    id: 'streak-3',
    name: '连续3天练习',
    description: '连续3天每天至少完成1次练习',
    icon: '🔥',
  },
  {
    id: 'streak-7',
    name: '连续7天练习',
    description: '连续7天每天至少完成1次练习',
    icon: '⚡',
  },
  {
    id: 'century',
    name: '百题斩',
    description: '累计答对100道题',
    icon: '💯',
  },
  {
    id: 'perfect',
    name: '满分',
    description: '在任意练习中获得100%正确率',
    icon: '🌟',
  },
  {
    id: 'night-owl',
    name: '夜猫子',
    description: '在午夜0点至4点之间完成练习',
    icon: '🦉',
  },
  {
    id: 'early-bird',
    name: '早起鸟',
    description: '在早上7点之前完成练习',
    icon: '🐦',
  },
  {
    id: 'speed-demon',
    name: '闪电侠',
    description: '在60秒内答完10道题',
    icon: '⚡',
  },
];

export function getAchievements(): Achievement[] {
  const storage = getStorage();
  return ACHIEVEMENT_DEFS.map((def) => ({
    ...def,
    earned: storage.earnedAchievements.includes(def.id),
    earnedAt: storage.achievementTimestamps[def.id],
  }));
}

export function earnAchievement(id: string): boolean {
  const storage = getStorage();
  if (storage.earnedAchievements.includes(id)) {
    return false; // Already earned
  }
  storage.earnedAchievements.push(id);
  storage.achievementTimestamps[id] = new Date().toISOString();
  saveStorage(storage);
  return true; // Newly earned
}

export function getUnearned(): Achievement[] {
  const storage = getStorage();
  return ACHIEVEMENT_DEFS.filter((def) => !storage.earnedAchievements.includes(def.id)).map(
    (def) => ({
      ...def,
      earned: false,
    })
  );
}

export function getStreakData(): StreakData {
  return getStorage().streakData;
}

export function updateStreak(): StreakData {
  const storage = getStorage();
  const today = new Date().toISOString().split('T')[0];
  const lastDate = storage.streakData.lastPracticeDate;

  if (lastDate === today) {
    // Already practiced today, no change
    return storage.streakData;
  }

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  if (lastDate === yesterdayStr) {
    // Continuing streak
    storage.streakData.current += 1;
  } else if (lastDate !== today) {
    // Streak broken, start new
    storage.streakData.current = 1;
  }

  if (storage.streakData.current > storage.streakData.best) {
    storage.streakData.best = storage.streakData.current;
  }

  storage.streakData.lastPracticeDate = today;
  saveStorage(storage);
  return storage.streakData;
}

export function getEarnedCount(): number {
  return getStorage().earnedAchievements.length;
}

export function getTotalCount(): number {
  return ACHIEVEMENT_DEFS.length;
}

export function getCumulativeStats(): { practiceCount: number; correctAnswers: number } {
  const storage = getStorage();
  return {
    practiceCount: storage.totalPracticeCount,
    correctAnswers: storage.totalCorrectAnswers,
  };
}

export function incrementCumulativeStats(
  practiceIncrement: number,
  correctIncrement: number
): { practiceCount: number; correctAnswers: number } {
  const storage = getStorage();
  storage.totalPracticeCount += practiceIncrement;
  storage.totalCorrectAnswers += correctIncrement;
  saveStorage(storage);
  return {
    practiceCount: storage.totalPracticeCount,
    correctAnswers: storage.totalCorrectAnswers,
  };
}
